import React, { useMemo, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  PickersDay,
  type PickersDayProps,
} from '@mui/x-date-pickers/PickersDay';
import {
  formatWeekLabel,
  isDayInWeek,
  isWeekRangeEnd,
  isWeekRangeMiddle,
  isWeekRangeStart,
  toWeekStartIso,
  weekStartIsoToDate,
} from '../../utils/hseWeek.util';
import { useTranslation } from 'react-i18next';

export interface ReportingWeekPickerProps {
  selectedWeekStart: string;
  onWeekChange: (weekStart: string) => void;
  disabled?: boolean;
}

type WeekPickersDayProps = PickersDayProps<Date> & {
  selectedWeekStart?: Date;
  hoveredDay?: Date | null;
  onDayHover?: (day: Date | null) => void;
};

type RangePalette = 'selected' | 'hover';

function rangeDaySx(
  day: Date,
  weekAnchor: Date,
  palette: RangePalette,
): Record<string, unknown> {
  if (!isDayInWeek(day, weekAnchor)) return {};

  const isStart = isWeekRangeStart(day, weekAnchor);
  const isEnd = isWeekRangeEnd(day, weekAnchor);
  const isMiddle = isWeekRangeMiddle(day, weekAnchor);

  if (palette === 'selected') {
    if (isStart || isEnd) {
      return {
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        fontWeight: 600,
        borderRadius: '50%',
        '&:hover, &:focus': {
          bgcolor: 'primary.dark',
        },
      };
    }
    if (isMiddle) {
      return {
        bgcolor: 'rgba(0, 0, 254, 0.12)',
        color: 'text.primary',
        borderRadius: 0,
        '&:hover, &:focus': {
          bgcolor: 'rgba(0, 0, 254, 0.18)',
        },
      };
    }
  }

  if (isStart || isEnd) {
    return {
      bgcolor: 'rgba(0, 0, 254, 0.2)',
      color: 'primary.main',
      fontWeight: 600,
      borderRadius: '50%',
      '&:hover, &:focus': {
        bgcolor: 'rgba(0, 0, 254, 0.28)',
      },
    };
  }
  if (isMiddle) {
    return {
      bgcolor: 'rgba(0, 0, 254, 0.08)',
      color: 'text.primary',
      borderRadius: 0,
      '&:hover, &:focus': {
        bgcolor: 'rgba(0, 0, 254, 0.14)',
      },
    };
  }

  return {};
}

const WeekPickersDay: React.FC<WeekPickersDayProps> = ({
  selectedWeekStart,
  hoveredDay,
  onDayHover,
  day,
  outsideCurrentMonth,
  onMouseEnter: _onMouseEnter,
  onMouseLeave: _onMouseLeave,
  ...other
}) => {
  const inSelectedWeek = selectedWeekStart ? isDayInWeek(day, selectedWeekStart) : false;
  const inHoveredWeek = hoveredDay ? isDayInWeek(day, hoveredDay) : false;
  const showHover = inHoveredWeek && !inSelectedWeek;
  const isRangeEndpoint =
    selectedWeekStart &&
    inSelectedWeek &&
    (isWeekRangeStart(day, selectedWeekStart) || isWeekRangeEnd(day, selectedWeekStart));

  return (
    <PickersDay
      {...other}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      selected={Boolean(isRangeEndpoint)}
      disableMargin={inSelectedWeek || inHoveredWeek}
      onMouseEnter={() => {
        onDayHover?.(day);
      }}
      onMouseLeave={() => {
        onDayHover?.(null);
      }}
      sx={{
        width: 36,
        height: 36,
        ...(inSelectedWeek && selectedWeekStart
          ? rangeDaySx(day, selectedWeekStart, 'selected')
          : {}),
        ...(showHover && hoveredDay ? rangeDaySx(day, hoveredDay, 'hover') : {}),
      }}
    />
  );
};

const ReportingWeekPicker: React.FC<ReportingWeekPickerProps> = ({
  selectedWeekStart,
  onWeekChange,
  disabled,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [hoveredDay, setHoveredDay] = useState<Date | null>(null);

  const weekValue = useMemo(() => {
    const parsed = weekStartIsoToDate(selectedWeekStart);
    return parsed ?? new Date();
  }, [selectedWeekStart]);

  const weekLabel = formatWeekLabel(selectedWeekStart);
  const tooltipTitle = t('hse.weekTooltip', { label: weekLabel });

  const calendarSlotSx = {
    '& .MuiPickersCalendarHeader-label': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
    '& .MuiPickersArrowSwitcher-button': {
      color: theme.palette.primary.main,
    },
    '& .MuiDayCalendar-weekDayLabel': {
      color: theme.palette.text.secondary,
      fontWeight: 500,
    },
  };

  return (
    <Tooltip title={tooltipTitle} arrow placement="top" enterDelay={300}>
      <Box sx={{ width: '100%', minWidth: 0 }}>
        <DatePicker
          label={t('hse.reportingWeek')}
          value={weekValue}
          onChange={(date) => {
            if (!date || Number.isNaN(date.getTime())) return;
            onWeekChange(toWeekStartIso(date));
          }}
          onClose={() => setHoveredDay(null)}
          disabled={disabled}
          slots={{ day: WeekPickersDay }}
          slotProps={{
            day: {
              selectedWeekStart: weekValue,
              hoveredDay,
              onDayHover: setHoveredDay,
            } as WeekPickersDayProps,
            textField: {
              size: 'small',
              fullWidth: true,
              helperText: t('hse.weekOf', { label: weekLabel }),
              FormHelperTextProps: {
                sx: { mx: 0, mt: 0.5, lineHeight: 1.4 },
              },
              sx: { width: '100%', minWidth: 0 },
            },
            desktopPaper: {
              sx: {
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
                boxShadow: theme.shadows[3],
                ...calendarSlotSx,
              },
            },
            mobilePaper: {
              sx: calendarSlotSx,
            },
            popper: {
              onMouseLeave: () => setHoveredDay(null),
            },
          }}
        />
      </Box>
    </Tooltip>
  );
};

export default ReportingWeekPicker;
